import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeResponse } from '@coffman-tm/shared';
import { ApiCallError, api } from './api';
import {
  ACTIVE_PROJECT_STORAGE_KEY,
  clearActiveProjectId,
  clearProjectReloadGuard,
} from './activeProject';
import { DEACTIVATED_NOTICE, clearAuthNotice, stashAuthNotice } from './authNotice';

const SESSION_STORAGE_KEY = 'coffman-tm:session';

/**
 * `requireAuth` distinguishes a deactivated account from an expired session.
 * Both spellings are accepted because the field is a bare machine code either
 * way and a wrong guess here degrades silently into "no explanation on the
 * login screen" — the exact failure this exists to prevent.
 */
export function isDeactivated(payload: { error?: string; reason?: string }): boolean {
  return payload.reason === 'account_deactivated' || payload.error === 'account_deactivated';
}

interface AuthState {
  /**
   * 'error' exists because the bootstrap used to have no way to fail. A non-401
   * /me response (500, 403, a timeout, the API being unreachable) threw out of
   * an unawaited promise: the rejection went to the console, `status` stayed
   * 'loading' forever, and ProtectedRoute rendered "Loading…" for the rest of
   * the session with no path forward. A failed bootstrap is now a state.
   */
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  me: MeResponse | null;
  token: string | null;
  /** Human-readable reason, set only when status === 'error'. */
  error: string | null;
}

interface AuthContextValue extends AuthState {
  /** Persist a fresh session and load /me. Called by VerifyPage after exchange. */
  setSession: (token: string) => Promise<void>;
  /** Sign out: revoke session server-side, clear local state. */
  signOut: () => Promise<void>;
  /** Re-run the bootstrap after an 'error'. Backs ProtectedRoute's Retry. */
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The three transport failures, as prose.
 *
 * Exported rather than inlined because the login form shows the same three
 * conditions on a different surface, and two hand-written copies of "couldn't
 * reach the server" WILL drift — at which point the app tells the same user two
 * different stories about the same outage. `authErrors.ts` imports these; there
 * is exactly one wording of each.
 */
export const TIMEOUT_MESSAGE =
  "The server didn't respond in time. It may be waking up — try again in a moment.";
export const NETWORK_MESSAGE = "Couldn't reach the server. Check your connection and try again.";
export const SERVER_ERROR_MESSAGE =
  'The server ran into a problem signing you in. Try again in a moment.';
export const UNKNOWN_ERROR_MESSAGE = 'Something went wrong signing you in. Try again.';

/**
 * Turn a failed /me into copy the PM can act on. 403 no_project_access is
 * called out by name because it is the one failure that retrying will never
 * fix — the session is valid, the account simply isn't on this project, and
 * the only way forward is a human granting access.
 */
export function bootstrapErrorMessage(err: unknown): string {
  if (err instanceof ApiCallError) {
    if (err.status === 403 && err.payload.error === 'no_project_access') {
      return "Your account isn't attached to this project. Ask an admin to grant access, then try again.";
    }
    if (err.status === 403 && err.payload.error === 'project_forbidden') {
      // api.ts clears the stored pick and reloads once; this copy only shows
      // if that recovery was latched off (second occurrence in one tab).
      return "You no longer have access to the project you had selected. Reload to return to your default project.";
    }
    if (err.kind === 'timeout') {
      return TIMEOUT_MESSAGE;
    }
    if (err.kind === 'network') {
      return NETWORK_MESSAGE;
    }
    if (err.status >= 500) {
      return SERVER_ERROR_MESSAGE;
    }
    return err.message;
  }
  return UNKNOWN_ERROR_MESSAGE;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    me: null,
    token: null,
    error: null,
  });
  // Bumping this re-runs the bootstrap effect — that's what Retry does.
  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  const loadMe = useCallback(async (token: string) => {
    try {
      const me = await api.me(token);
      // A good /me means the active-project selection (if any) was accepted —
      // release the one-shot stale-selection reload latch so a LATER
      // revocation in this same tab can still self-heal.
      clearProjectReloadGuard();
      setState({ status: 'authenticated', me, token, error: null });
    } catch (err) {
      // 401 is the one failure that means "this session is dead" — clear it and
      // fall through to the login screen.
      if (err instanceof ApiCallError && err.status === 401) {
        // Deactivation is the ONE 401 that isn't "log back in": the password
        // will be right and it still won't work, because login answers every
        // anonymous failure with the same string to avoid being an enumeration
        // oracle. Carry the real reason across the redirect ourselves.
        if (isDeactivated(err.payload)) stashAuthNotice(DEACTIVATED_NOTICE);
        localStorage.removeItem(SESSION_STORAGE_KEY);
        // The next person to sign in on this browser must not inherit a
        // project selection they may have no membership on.
        clearActiveProjectId();
        setState({ status: 'anonymous', me: null, token: null, error: null });
        return;
      }
      // Everything else is an error we can *show*. Deliberately not rethrown:
      // the caller is an unawaited `void loadMe(...)`, so a throw here would
      // become an unhandled rejection and leave the UI stuck on 'loading'.
      // The token is kept — the session may well still be good.
      setState({ status: 'error', me: null, token, error: bootstrapErrorMessage(err) });
    }
  }, []);

  // Bootstrap on mount: read token from localStorage and validate via /me.
  useEffect(() => {
    const token = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      setState({ status: 'anonymous', me: null, token: null, error: null });
      return;
    }
    void loadMe(token);
  }, [loadMe, bootstrapNonce]);

  /**
   * The project pick is shared localStorage, so a switch in ANOTHER tab silently
   * changes what THIS tab sends. Without this, tab A would keep rendering its
   * old project's header and rail while its next request carried the new
   * project id — the same screen showing two projects' data.
   *
   * Reload rather than re-fetch, for the same reason switching does (§A4): every
   * cached list in the tree is project-scoped. `storage` only fires in the tabs
   * that did NOT make the change, so the switching tab never double-navigates.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVE_PROJECT_STORAGE_KEY) return;
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const retry = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    setBootstrapNonce((n) => n + 1);
  }, []);

  const setSession = useCallback(
    async (token: string) => {
      // A fresh session may belong to a different person on this browser. Start
      // them on their own default project rather than inheriting the previous
      // user's pick (which at best flashes a project_forbidden on first login).
      clearActiveProjectId();
      localStorage.setItem(SESSION_STORAGE_KEY, token);
      setState({ status: 'loading', me: null, token, error: null });
      await loadMe(token);
    },
    [loadMe],
  );

  const signOut = useCallback(async () => {
    const token = state.token;
    setState({ status: 'anonymous', me: null, token: null, error: null });
    localStorage.removeItem(SESSION_STORAGE_KEY);
    clearActiveProjectId();
    // Signing out on purpose is not a failure — it must not inherit a notice
    // stashed by whatever killed a previous session in this tab.
    clearAuthNotice();
    if (token) {
      try {
        await api.logout(token);
      } catch {
        // Server-side revocation failed; the client state is already cleared.
      }
    }
  }, [state.token]);

  return (
    <AuthContext.Provider value={{ ...state, setSession, signOut, retry }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
